import { nothing, unsafeCSS, html } from "lit";
import { property, state, query, queryAssignedElements } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { classMap } from "lit/directives/class-map.js";
import { scrollIntoViewWithOffset } from "../../utilities/anchor.js";
import { handleRippleKeyboard, handleRippleMouse } from "../../utilities/animations.js";
import { isMobile } from "../../utilities/is-mobile.js";
import { watch } from "../../utilities/watch.js";
import { LeafElement } from "../leaf-element.js";
import { LeafSelect } from "../select/select.component.js";
import { LeafSelectOption } from "../select-option/select-option.component.js";
const styles = "/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  display: block;\n  min-width: 0;\n}\n\n.leaf-c-tab {\n  position: relative;\n}\n.leaf-c-tab .border-bar {\n  position: absolute;\n  box-shadow: 0 var(--leaf-border-width-tab-default) var(--leaf-color-border-brand-strong);\n  bottom: 0;\n  height: var(--leaf-border-width-tab-default);\n  z-index: calc(var(--leafdcws-z-index-100) + 1);\n}\n.leaf-c-tab .tabs-wrapper {\n  background-color: var(--leaf-color-bg-opacity-transparent);\n  display: flex;\n  height: 100%;\n  position: relative;\n  overflow-x: hidden;\n}\n.leaf-c-tab .tabs-container {\n  overflow-y: hidden;\n  width: 100%;\n}\n@media (max-width: 47.999rem) {\n  .leaf-c-tab .tabs-container {\n    margin-right: 0;\n    padding-left: 0;\n  }\n}\n.leaf-c-tab .border-wrapper {\n  border-bottom: var(--leaf-border-width-tab-default) solid var(--leaf-color-border-default);\n  display: flex;\n  position: relative;\n  overflow: visible;\n  min-width: -moz-fit-content;\n  min-width: fit-content;\n  width: 100%;\n}\n.leaf-c-tab .tabs {\n  display: inline-flex;\n  border-bottom: var(--leaf-border-width-tab-default) solid var(--leaf-color-border-default);\n  margin-bottom: calc(0px - var(--leaf-border-width-tab-default));\n  margin-right: auto;\n  margin-left: 0;\n}\n.leaf-c-tab #tabs-select {\n  display: none;\n}\n\n.leaf-c-tab--align-center .tabs {\n  margin-left: auto;\n  margin-right: auto;\n}\n\n.leaf-c-tab--align-right .tabs {\n  margin-left: auto;\n  margin-right: 0;\n}\n\n@media (max-width: 47.999rem) {\n  .leaf-c-tab--mobile-dropdown .border-wrapper {\n    display: none;\n  }\n  .leaf-c-tab--mobile-dropdown #tabs-select {\n    display: block;\n  }\n}\n\n.leaf-c-tab--align-justified .tabs {\n  width: 100%;\n}\n\n.tabs-wrapper {\n  --mask: unset;\n  display: flex;\n  margin-bottom: var(--leaf-size-ml);\n  position: relative;\n  mask: var(--mask);\n  -webkit-mask: var(--mask);\n}\n.tabs-wrapper.fade-right {\n  --mask: linear-gradient(to right, rgba(0, 0, 0, 1) calc(100% - var(--leaf-spacing-24)), rgba(0, 0, 0, 0) 100%);\n}\n.tabs-wrapper.fade-left {\n  --mask: linear-gradient(to left, rgba(0, 0, 0, 1) calc(100% - var(--leaf-spacing-24)), rgba(0, 0, 0, 0) 100%);\n}\n.tabs-wrapper.fade-left.fade-right {\n  --mask: linear-gradient(\n    to left,\n    rgba(0, 0, 0, 0) 0,\n    rgba(0, 0, 0, 1) var(--leaf-spacing-24),\n    rgba(0, 0, 0, 1) calc(100% - var(--leaf-spacing-24)),\n    rgba(0, 0, 0, 0) 100%\n  );\n}\n\n.tabs {\n  display: inline-flex;\n  margin: 0;\n  padding: 0;\n}\n@media (max-width: 47.999rem) {\n  .tabs {\n    padding-left: 0;\n  }\n}\n\n.tabs-container {\n  overflow-x: auto;\n  -ms-overflow-style: none; /* IE and Edge */\n  scrollbar-width: none; /* Firefox */\n  margin: auto 0;\n  padding-left: 0;\n}\n.tabs-container::-webkit-scrollbar {\n  display: none;\n}\n@media (max-width: 47.999rem) {\n  .tabs-container {\n    margin-right: 0;\n    padding-left: 0;\n  }\n}\n\n.tabs {\n  list-style: none;\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _LeafTab = class _LeafTab extends LeafElement {
  constructor() {
    super(...arguments);
    this.mobileVariant = "scroll";
    this.tabAlign = "left";
    this.isNested = false;
    this.isOverflowing = false;
    this.selectOptionArray = [];
    this.isMobile = isMobile("md");
    this.scrollToTab = () => {
      let scrollTopPosition = this.getBoundingClientRect().top + window.scrollY;
      const activeTab = this.querySelectorAll(`:scope > leaf-tab-item[label="${this.hash}"][active]`);
      if (activeTab && activeTab.length > 0) {
        setTimeout(() => {
          window.scrollTo({
            top: scrollTopPosition,
            left: 0,
            behavior: "smooth"
          });
        }, 100);
        const stickyBannerElement = document.querySelector("chc-sticky-banner");
        if (stickyBannerElement) {
          window.onscrollend = () => {
            var _a, _b;
            window.onscrollend = null;
            scrollTopPosition = (_b = (_a = stickyBannerElement.shadowRoot) == null ? void 0 : _a.querySelector(".chc-c-sticky-banner")) == null ? void 0 : _b.offsetHeight;
            scrollIntoViewWithOffset("leaf-tab", scrollTopPosition);
          };
        }
      }
    };
    this.setActiveTabInView = () => {
      const activeTab = this.querySelector(`leaf-tab-item[active]`);
      if (activeTab) {
        const activeTabRect = activeTab.getBoundingClientRect();
        const containerRect = this.tabsContainer.getBoundingClientRect();
        const scrollLeftPosition = activeTabRect.left - containerRect.left + this.tabsContainer.scrollLeft;
        this.tabsContainer.scrollTo({
          top: 0,
          left: scrollLeftPosition,
          behavior: "smooth"
        });
      }
    };
    this.setOverflowing = () => {
      if (this.mobileVariant === "scroll" || !this.isMobile) {
        this.isOverflowing = this.tabsContainer.scrollWidth > this.tabsContainer.clientWidth;
        this.handleScroll();
      } else {
        this.isOverflowing = false;
        this.tabsWrapper.classList.remove("fade-left");
        this.tabsWrapper.classList.remove("fade-right");
      }
    };
    this.handleScroll = () => {
      const isScrolledToRight = this.tabsContainer.scrollWidth <= Math.ceil(this.tabsContainer.clientWidth + this.tabsContainer.scrollLeft);
      const isScrolledToLeft = this.tabsContainer.scrollLeft === 0;
      this.tabsWrapper.classList.toggle("fade-left", !isScrolledToLeft);
      this.tabsWrapper.classList.toggle("fade-right", !isScrolledToRight);
    };
    this.setActiveTabWithHash = (childArray, urlSections) => {
      childArray.forEach((element) => {
        element.removeAttribute("active");
        element.removeAttribute("aria-selected");
      });
      urlSections.forEach((element) => {
        element.setAttribute("active", "true");
        element.setAttribute("aria-selected", "true");
      });
    };
    this.setActiveTabs = (label) => {
      var _a;
      this.hash = label;
      const newDesktopTarget = this.querySelector(`leaf-tab-item[label='${label}']`);
      const mobileDropdown = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector(`leaf-select`);
      const newMobileTarget = mobileDropdown == null ? void 0 : mobileDropdown.querySelector(`leaf-select-option[value='${label}']`);
      const activeTab = this.querySelector(`leaf-tab-item[active]`);
      const activeTabLabel = activeTab == null ? void 0 : activeTab.getAttribute("label");
      const activeSection = this.querySelector(`leaf-tab-section[label='${activeTabLabel}']`);
      activeTab == null ? void 0 : activeTab.removeAttribute("active");
      activeTab == null ? void 0 : activeTab.removeAttribute("aria-selected");
      const activeTabMobile = this.querySelector(`leaf-select-option[selected]`);
      activeTabMobile == null ? void 0 : activeTabMobile.removeAttribute("selected");
      activeSection == null ? void 0 : activeSection.removeAttribute("active");
      activeSection == null ? void 0 : activeSection.setAttribute("aria-hidden", "true");
      newMobileTarget == null ? void 0 : newMobileTarget.setAttribute("selected", "true");
      mobileDropdown == null ? void 0 : mobileDropdown.setAttribute("value", label);
      newDesktopTarget == null ? void 0 : newDesktopTarget.setAttribute("active", "true");
      const targetSection = this.querySelector(`leaf-tab-section[label='${label}']`);
      targetSection == null ? void 0 : targetSection.setAttribute("active", "true");
      targetSection == null ? void 0 : targetSection.setAttribute("aria-hidden", "false");
      if (this.mobileVariant === "scroll" || !this.isMobile) {
        this.scrollToTab();
      }
    };
    this.handleDropdownChange = (event) => {
      const target = event.target;
      const targetLabel = target == null ? void 0 : target.getAttribute("value");
      targetLabel ? this.setActiveTabs(targetLabel) : nothing;
    };
    this.setDropdownArray = (nodeList) => {
      var _a;
      if (this.mobileVariant === "dropdown") {
        this.selectOptionArray = [];
        nodeList.forEach((node) => {
          const tabItem = node;
          const selectOption = document.createElement("leaf-select-option");
          const text = tabItem.textContent || "";
          selectOption.setAttribute("value", tabItem.getAttribute("label") || "");
          selectOption.textContent = text;
          tabItem.hasAttribute("disabled") ? selectOption.setAttribute("disabled", "true") : nothing;
          if (this.hash) {
            selectOption.value === this.hash ? selectOption.setAttribute("selected", "true") : nothing;
          }
          this.selectOptionArray.push(selectOption);
        });
        if (this.hash) {
          const tabItem = this.querySelector(`leaf-tab-item[label='${this.hash}']`);
          if (tabItem) {
            const parentTab = tabItem == null ? void 0 : tabItem.closest("leaf-tab");
            if ((parentTab == null ? void 0 : parentTab.isNested) && !this.isNested) {
              this.selectedValue = ((_a = parentTab == null ? void 0 : parentTab.closest("leaf-tab-section")) == null ? void 0 : _a.getAttribute("label")) ?? this.hash;
            } else {
              this.selectedValue = this.hash;
            }
          }
        }
      }
    };
    this.updateDropdown = async () => {
      var _a;
      this.setDropdownArray([...this.querySelectorAll('[slot="tab"]')]);
      await this.updateComplete;
      (_a = this.shadowRoot) == null ? void 0 : _a.querySelectorAll("leaf-select-option").forEach((option) => {
        option.emit("leaf:select-option-change");
      });
    };
    this.handleDropdownSlotchange = ({ target }) => {
      this.setDropdownArray(target == null ? void 0 : target.assignedNodes());
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.hash = window.location.hash.replace("#", "");
    window.addEventListener("resize", this.handleResize.bind(this));
    this.addEventListener("keydown", this.handleKeypress);
    this.addEventListener("leaf-click", this.handleTabClick);
    this.addEventListener("leaf-keydown", this.handleTabKeydown);
    this.addEventListener("leaf-mousedown", this.handleTabMousedown);
    this.addEventListener("leaf-tabs:dropdown-change", this.updateDropdown);
  }
  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize.bind(this));
    this.removeEventListener("keydown", this.handleKeypress);
    this.removeEventListener("leaf-click", this.handleTabClick);
    this.removeEventListener("leaf-keydown", this.handleTabKeydown);
    this.removeEventListener("leaf-mousedown", this.handleTabMousedown);
    this.removeEventListener("leaf-tabs:dropdown-change", this.updateDropdown);
    super.disconnectedCallback();
  }
  async firstUpdated(changedProperties) {
    var _a;
    super.firstUpdated(changedProperties);
    await this.updateComplete;
    (_a = this.tabsContainer) == null ? void 0 : _a.addEventListener("scroll", this.handleScroll);
    let urlSections;
    if (this.hash) {
      urlSections = this.querySelectorAll(`:scope > [label='${this.hash}']`);
    }
    if (urlSections && urlSections.length > 0) {
      const childArray = Array.from(this.children);
      this.setActiveTabWithHash(childArray, [...urlSections]);
      if (this.isNested) {
        const parentElement = this.closest("leaf-tab-section");
        const parentTab = parentElement == null ? void 0 : parentElement.closest("leaf-tab");
        const parentTabItemLabel = parentElement == null ? void 0 : parentElement.getAttribute("label");
        if (parentTab) {
          const parentTabUrlSections = parentTab.querySelectorAll(`:scope > [label='${parentTabItemLabel}']`);
          const parentTabChildArray = Array.from(parentTab.children);
          this.setActiveTabWithHash(parentTabChildArray, [...parentTabUrlSections]);
        }
      }
      setTimeout(() => {
        this.scrollToTab();
        this.setActiveTabInView();
      }, 100);
    }
  }
  async updated(changedProperties) {
    super.updated(changedProperties);
    await this.updateComplete;
    const activeTab = this.querySelector(`leaf-tab-item[active]`);
    const resizeObserver = new ResizeObserver(async (entries) => {
      if (entries[0]) {
        this.handleResize();
      }
    });
    resizeObserver.observe(activeTab);
    this.setOverflowing();
  }
  handleDropdown() {
    this.requestUpdate();
  }
  handleResize() {
    this.isMobile = isMobile("md");
    if (this.mobileVariant === "scroll" || !this.isMobile) {
      this.setOverflowing();
      this.setActiveTabInView();
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleTabKeydown(event) {
    const { detail } = event;
    handleRippleKeyboard(detail);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleTabMousedown(event) {
    const { detail } = event;
    handleRippleMouse(detail);
  }
  handleTabClick(event) {
    const target = event.target;
    if (this.isOverflowing) {
      target.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    }
    if (target.parentElement === this) {
      const targetLabel = target.getAttribute("label");
      targetLabel ? this.setActiveTabs(targetLabel) : nothing;
    }
  }
  handleKeypress(event) {
    var _a, _b;
    const focusFunction = (event2, isLeft) => {
      var _a2, _b2;
      event2.preventDefault();
      const allTabsLen = (_a2 = this.allTabs) == null ? void 0 : _a2.length;
      const thisElement = event2.target;
      const currentPosition = Array.from(this.allTabs).indexOf(thisElement);
      const previousPosition = this.allTabs[(currentPosition + allTabsLen - 1) % allTabsLen];
      const nextPosition = this.allTabs[(currentPosition + 1) % allTabsLen];
      const nextElement = isLeft ? previousPosition : nextPosition;
      if (nextElement && nextElement.nodeName == "LEAF-TAB-ITEM") {
        const nextTab = (_b2 = nextElement.shadowRoot) == null ? void 0 : _b2.querySelector(".tab-item");
        nextTab.focus();
        nextTab.scrollIntoView({ block: "nearest", inline: "nearest" });
        this.handleScroll();
      }
    };
    switch (event.key) {
      case "ArrowRight":
        focusFunction(event, false);
        break;
      case "ArrowDown":
        ((_b = (_a = this.querySelector("leaf-tab-section[active]")) == null ? void 0 : _a.shadowRoot) == null ? void 0 : _b.querySelector("section")).focus();
        break;
      case "ArrowLeft":
        focusFunction(event, true);
        break;
    }
  }
  render() {
    const componentClassName = {
      "leaf-c-tab": true,
      "leaf-c-tab--mobile-dropdown": this.mobileVariant === "dropdown",
      "leaf-c-tab--align-center": this.tabAlign === "center",
      "leaf-c-tab--align-right": this.tabAlign === "right",
      "leaf-c-tab--align-justified": this.tabAlign === "justified",
      "leaf-c-tab--scroll": this.isOverflowing
    };
    return html`<div class=${classMap(componentClassName)}>
      <div class="tabs-wrapper">
        <div class="tabs-container">
          ${this.mobileVariant === "dropdown" && this.selectOptionArray.length >= 1 ? html`<leaf-select
                  id="tabs-select"
                  name="tabs-select"
                  value=${ifDefined(this.selectedValue)}
                  required
                  @change=${this.handleDropdownChange}
                  >${this.selectOptionArray}</leaf-select
                >` : nothing}
          <div class="border-wrapper">
        <ul class="tabs" id="tabset" role="tablist">
          <slot name="tab" @slotchange=${this.handleDropdownSlotchange}></slot>
        </ul>
      </div>
          </div>
        </div>
        <div class="tab-content" id="tabset-content">
          <slot name="tab-section"></slot>
        </div>
      </div>
    </div>`;
  }
};
_LeafTab.styles = unsafeCSS(styles);
_LeafTab.elementDefinitions = {
  "leaf-select": LeafSelect,
  "leaf-select-option": LeafSelectOption
};
let LeafTab = _LeafTab;
__decorateClass([
  property({ attribute: "mobile-variant" })
], LeafTab.prototype, "mobileVariant", 2);
__decorateClass([
  property({ attribute: "tab-align" })
], LeafTab.prototype, "tabAlign", 2);
__decorateClass([
  property({ type: Boolean, attribute: "nested" })
], LeafTab.prototype, "isNested", 2);
__decorateClass([
  state()
], LeafTab.prototype, "isOverflowing", 2);
__decorateClass([
  state()
], LeafTab.prototype, "selectOptionArray", 2);
__decorateClass([
  state()
], LeafTab.prototype, "isMobile", 2);
__decorateClass([
  state()
], LeafTab.prototype, "selectedValue", 2);
__decorateClass([
  state()
], LeafTab.prototype, "hash", 2);
__decorateClass([
  query(".leaf-c-tab")
], LeafTab.prototype, "leafTab", 2);
__decorateClass([
  query(".tabs-wrapper")
], LeafTab.prototype, "tabsWrapper", 2);
__decorateClass([
  query(".tabs-container")
], LeafTab.prototype, "tabsContainer", 2);
__decorateClass([
  queryAssignedElements({ slot: "tab" })
], LeafTab.prototype, "allTabs", 2);
__decorateClass([
  watch(["isMobile", "mobileVariant"])
], LeafTab.prototype, "handleDropdown", 1);
export {
  LeafTab
};
//# sourceMappingURL=tabs.component.js.map
